import React, { useMemo } from 'react';
import { TypeaheadField } from '../table/TypeaheadField';
import { CategoryOption } from '../table/inlineCategoryOptions';

/** A Northstar Dropdown item as the ticket forms build them (formatProperty): its name and its id. */
export interface DropdownItem {
  header: string;
  accessibilityitemprops: { id: number } | number;
  [key: string]: unknown;
}

const itemId = (item: DropdownItem): number =>
  typeof item.accessibilityitemprops === 'number' ? item.accessibilityitemprops : item.accessibilityitemprops?.id;

interface Props {
  /** For the screen reader; the form draws the label itself (with its required star). */
  label: string;
  items: DropdownItem[];
  /** The field's value, as the form holds it. */
  valueId: number | null | undefined;
  /** Shown when the value is not among `items` - an assignee outside the chosen group, say - as the old box showed it. */
  valueName?: string;
  /** Called with the picked item (null when cleared) and its id (0 when cleared), as the Dropdown's onChange gave them. */
  onChange: (item: DropdownItem | null, id: number) => void;
  disabled?: boolean;
  clearable?: boolean;
  /** Red while empty. */
  required?: boolean;
  placeholder?: string;
}

/**
 * The ticket list's typable picker for a field the ticket forms draw with Northstar's Dropdown (Team, Group, Assignee):
 * typing filters, the best match is lit, and a value is taken when picked or when the box is left on an exact name.
 * The form's own handlers are given the item and id the Dropdown gave them, so their knock-on rules (a new Team resets
 * Group and Assignee) are unchanged.
 */
export const TypeaheadItemPicker = ({ label, items, valueId, valueName, onChange, disabled, clearable, required, placeholder }: Props) => {
  const options = useMemo<CategoryOption[]>(() => {
    const built = (items ?? []).filter(item => item?.header).map(item => ({ id: itemId(item), name: String(item.header) }));
    return valueId != null && valueName && !built.some(option => option.id === valueId) ? [...built, { id: valueId, name: valueName }] : built;
  }, [items, valueId, valueName]);

  return (
    <TypeaheadField
      label={label}
      hideLabel
      listFitsBox
      options={options}
      valueId={valueId ?? null}
      onChange={id => {
        if (id == null) { onChange(null, 0); return; }
        const picked = (items ?? []).find(item => itemId(item) === id);
        // A value only kept for display (valueName) is not a choice the list offers - nothing to hand back.
        if (picked) onChange(picked, id);
      }}
      placeholder={placeholder}
      clearable={clearable}
      required={required}
      disabled={disabled}
      liveChange={false}
    />
  );
};
